---
id: apps-app-enterprise-doc
title: App Enterprise
owner: Luc Lemerez
status: Active
lastReviewed: 2026-01-27
visibility: public
tags:
  - app
  - app-enterprise
  - enterprise
  - frontend
---

## Product Overview

Enterprise web application for Rezi that supports organization-scoped experiences, including onboarding, operational settings, and enterprise resume workflows.

### Primary Workflow
Users authenticate, complete an enterprise onboarding flow, and then operate within the dashboard, account, settings, and deployment areas that are scoped to their organization.

### Business Logic
Access is role-gated, with authenticated users required for all primary routes and admin-only paths reserved for elevated roles. Organization context drives what data is shown and which operational settings are available.

### Outcomes
This app enables enterprise administrators to configure deployments, manage users and organization settings, and use enterprise resume tooling in a governed environment.

### Out of Scope
Consumer-facing Rezi experiences and unauthenticated flows beyond sign-in are not part of this application.

## Technical Overview

React-based web application structured around guarded routes and organization-scoped layouts. The main router is defined in `apps/app-enterprise/src/app/app.tsx`, with the React bootstrap in `apps/app-enterprise/src/index.js` and shared layout orchestration in `apps/app-enterprise/src/layouts/dashboard-layout.tsx`.

### Entry Points
Routing is handled by `createBrowserRouter` in `apps/app-enterprise/src/app/app.tsx`, with lazy-loaded routes for onboarding, dashboard, account, settings, deployment, resume builder, resume review, rezi-admin, and talent search pages. Public access is limited to sign-in via `apps/app-enterprise/src/app/signin`, while authenticated and admin-only gates live in `apps/app-enterprise/src/utils/protected-route` and `apps/app-enterprise/src/utils/protected-admin-route`.

### Key Modules
Enterprise workflows live under `apps/app-enterprise/src/app/protected`, including deployment settings (`deployment/settings`), user and revenue views (`deployment/users`, `deployment/revenue`), organization settings (`settings/organization`), resume tooling (`resume-builder`, `resume-review`), and admin tooling (`rezi-admin`, `talent-search`). Layouts and shared UI are centralized in `apps/app-enterprise/src/layouts` and `apps/app-enterprise/src/ui`, with state managed by stores in `apps/app-enterprise/src/stores`.

### Data and Services
Firebase Auth, Firestore, and Storage are used via `@rezi/config/firebase`, with API hooks and stores in `apps/app-enterprise/src/hooks` and `apps/app-enterprise/src/stores` coordinating data access. Localization is configured via i18next in `apps/app-enterprise/src/index.js` and the locale bundles under `apps/app-enterprise/public/locales`.

### Integrations and Side Effects
The app integrates with enterprise auth providers and deployment settings under `apps/app-enterprise/src/app/protected/deployment/settings/advanced`, and uses Netlify configuration for deployment via `apps/app-enterprise/netlify.toml`.

### Constraints and Risks
Authentication, role checks, and organization context are critical-path concerns because they gate route access and data visibility across the enterprise surface.

## Changelog

- 2026-01-27: Expanded product and technical overviews to cover business logic and key code paths.
- 2026-01-27: Initial documentation.
